import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Megaphone, ShieldCheck, Clock } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchConsolePromos, type ConsolePromoRow } from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA promotions board (Epic 2) — the customer-facing promo catalogue with
// its dual approval gate: a promo only goes live once both Finance and Direction
// sign off. Reads go through fn_console_promos (SECURITY DEFINER, migration 140)
// so no cost/margin ever leaves the engine; a missing djayma schema simply errs.
// ---------------------------------------------------------------------------

// French label map for promo lifecycle statuses; falls back to the raw value.
const STATUS_LABELS: Record<string, string> = {
  draft: "Brouillon",
  pending: "En attente",
  active: "Active",
  paused: "En pause",
  ended: "Terminée",
};

/** fr-FR date, or an em dash when the bound is open (null). */
function fmtDate(d: string | null): string {
  return d ? new Date(d).toLocaleDateString("fr-FR") : "—";
}

/** Join both target arrays; "Toutes" when neither shop types nor zones are set. */
function fmtTargets(row: ConsolePromoRow): string {
  const parts = [...(row.target_shop_types ?? []), ...(row.target_zones ?? [])];
  return parts.length > 0 ? parts.join(", ") : "Toutes";
}

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

/** Small approval pill — emerald when granted, stone when still pending. */
function ApprovalBadge({ label, ok }: { readonly label: string; readonly ok: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        ok ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"
      }`}
    >
      {label}
    </span>
  );
}

export default function PromoBoard() {
  const q = useQuery({ queryKey: ["djayma", "console-promos"], queryFn: fetchConsolePromos, retry: false });

  const rows = q.data ?? [];
  const activeCount = rows.filter((r) => r.status === "active").length;
  const awaitingCount = rows.filter((r) => !(r.finance_approved && r.direction_approved)).length;
  const approvedCount = rows.filter((r) => r.finance_approved && r.direction_approved).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Megaphone className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Promotions</h1>
            <p className="text-sm text-stone-500">
              Catalogue promotionnel — fenêtres, ciblage et double approbation
            </p>
          </div>
        </div>

        {q.isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Promotions non joignables. Vérifiez les migrations 131-140 et l&apos;exposition du schéma
            <code className="mx-1 rounded bg-amber-100 px-1">djayma</code>.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            icon={<Megaphone className="h-5 w-5" />}
            tone="bg-stone-100 text-stone-700"
            label="Promotions au total"
            value={rows.length}
          />
          <Kpi
            icon={<Megaphone className="h-5 w-5" />}
            tone="bg-emerald-100 text-emerald-700"
            label="Actives"
            value={activeCount}
          />
          <Kpi
            icon={<Clock className="h-5 w-5" />}
            tone="bg-amber-100 text-amber-700"
            label="En attente d'approbation"
            value={awaitingCount}
          />
          <Kpi
            icon={<ShieldCheck className="h-5 w-5" />}
            tone="bg-emerald-100 text-emerald-700"
            label="Entièrement approuvées"
            value={approvedCount}
          />
        </div>

        {/* Promotions table */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Promotions</h2>
          {rows.length === 0 ? (
            <p className="text-sm text-stone-400">Aucune promotion.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Nom</th>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                    <th className="py-2 pr-4 font-medium">Fenêtre</th>
                    <th className="py-2 pr-4 font-medium">Cibles</th>
                    <th className="py-2 pr-4 font-medium text-right">Max/boutique</th>
                    <th className="py-2 pr-4 font-medium">Approbations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-4 text-stone-700">{r.name}</td>
                      <td className="py-2 pr-4 text-stone-500">{STATUS_LABELS[r.status] ?? r.status}</td>
                      <td className="py-2 pr-4 text-stone-500">
                        {fmtDate(r.starts_at)} → {fmtDate(r.ends_at)}
                      </td>
                      <td className="py-2 pr-4 text-stone-500">{fmtTargets(r)}</td>
                      <td className="py-2 pr-4 text-right text-stone-500">{r.max_redemptions_per_shop}</td>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-1.5">
                          <ApprovalBadge label="Finance" ok={r.finance_approved} />
                          <ApprovalBadge label="Direction" ok={r.direction_approved} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
